import { useDeferredValue, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
// U11 (E1) — the scoped copy-draft subscription below is shallow-compared.
import { useShallow } from 'zustand/react/shallow';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import { Icon } from '@clay-run/glaze/icon';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@clay-run/glaze/table';
import { Text } from '@clay-run/glaze/text';
import { Button } from '@clay-run/glaze/button';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { Alert } from '@clay-run/glaze/alert';

import { useApi } from '../../../_shared/hooks';
import { useLauncherCampaign } from '../../launcher-api';
import { useLauncherMembers } from '../../launcher-api';
import { getPlaybook, getSequence } from '../../api-client';
import { playbookDetailQueryKey, sequenceDetailQueryKey } from '../../query-keys';
import { MemberDetailDrawer } from './MemberDetailDrawer';
import {
  MemberCreatedCell,
  MemberDetailsButton,
  MemberEmailCell,
  MemberNameCell,
  MemberStatusCell,
  memberDisplay,
} from './MemberRowCells';
import {
  missingTokensByMemberId,
  rosterTokenGaps,
  sortMembersForScope,
} from '../../copy-token-flags';
import {
  selectGapSortDirection,
  setGapSortDirection,
  useMembersPanelCollapseStore,
} from '../../members-panel-collapse-store';
import { effectiveSender } from '../../sender-choice';
import {
  membersPanelState,
  matchedIdsForRungScope,
  matchedIdsForRungDraftScope,
  rungDraftSegmentationIsShared,
  RUNG_DRAFT_SHARED_SEGMENTATION_NOTE,
  type ScopedMatchState,
} from '../../members-panel-scope';
import { rungDraftKey } from '../../playbook-draft';
import { selectPlaybookDraft, usePlaybookDraftStore } from '../../playbook-draft-store';
import {
  useRungScopeStore,
  selectRungScope,
  selectAudienceDraft,
  rungScopeLabel,
  clearRungScope,
} from '../../rung-scope-store';
import {
  copyDraftKey,
  resolveCopyDraftValues,
  selectCopyDraftEntriesFor,
  useSequenceCopyDraftStore,
} from '../../sequence-copy-draft-store';
import {
  selectPreviewMember,
  selectPreviewMemberId,
  setSequenceEditorMode,
  useSequencePreviewStore,
} from '../../sequence-preview-store';
import {
  useAudienceMatchedMembers,
  useFilterMatchedMembers,
  useMatchedMembersLadder,
} from '../../use-matched-members';

/**
 * Launcher V2 — the persistent in-pane members table, Spencer's resolved
 * answer to the plan's Outstanding Question 1: a real Glaze `Table`, never a
 * roster sidebar, never a per-group 3-row sample.
 *
 * ── MOUNTING (PR B follow-up U5, R5): `PlayFlowShell` builds ONE element of
 * this component and hands it to each step (`QualifyStep`/`SequencesStep`/
 * `ReviewStep`) as a `membersPanel` render-slot prop; each step places it in
 * its own left column/rail instead of it rendering full-width above all three
 * steps. That means this component DOES unmount and remount on every tab
 * switch (Glaze `Tabs` unmounts inactive `TabPanel`s) — unlike A2/PR B, where
 * it lived outside the `Tabs` tree and never unmounted. "Persists across step
 * switches" is still true, just via a different mechanism: this component
 * holds NO `useState` of its own — the scope lives in the module-scope
 * `rung-scope-store` zustand store, and every read (`useLauncherMembers`,
 * `useMatchedMembersLadder`, `useAudienceMatchedMembers`, the playbook query)
 * is a React Query cache entry keyed independently of this component's
 * lifetime. A remount just re-subscribes to the same store and re-reads the
 * same cache, rendering identically; the only casualty is the inner table's
 * scroll offset (accepted). Do not add component-local state here without
 * re-reading this comment — it would silently reset on every tab switch.
 * See `rung-scope-store.ts` for why the scope (and the Audience Rule draft
 * this panel previews) is a module-scope zustand store in the first place.
 *
 * ── PR B U3 (R4): THE TABLE IS SCOPED FOR REAL. A2 shipped this as
 * label-only — the label named a rung and the rows stayed the full roster,
 * because matched-member evaluation didn't exist yet. It does now:
 *
 *   • no scope → the full campaign roster (unchanged A2 behavior),
 *   • a rung's Preview → that rung's REAL first-match-wins claim, via U2's
 *     ladder endpoint (`useMatchedMembersLadder`). Ladder, not the standalone
 *     per-segmentation match, on purpose: the waterfall's promise is "members
 *     go to the FIRST group whose conditions match", so a preview must exclude
 *     members a higher-priority rung already claimed — and a catch-all rung,
 *     which has no conditions, would otherwise "match" the entire roster.
 *     An UNMAPPED rung previews its real matches like any other (matching runs
 *     regardless of a sequence; unmapped-ness changes who gets a SEND).
 *   • the Audience Rule → its staged, unsaved draft evaluated against real
 *     members (plan Decision 3 — preview only; persisting is Phase 1).
 *
 * While a scope is set this panel never falls back to the roster: showing 40
 * rows under a "routed to <rung>" label would be a fabricated routing claim.
 * The loading / couldn't-match / matched-zero branches below are that rule,
 * and the decision itself is the pure `membersPanelState` (unit-tested in
 * `__tests__/v2-play-flow-shell.test.tsx`).
 *
 * ── PR C QA follow-ups U6 (items 8, 9): when a scope is set this panel gets
 * the SAME accent `RungRow` tints its previewed row with
 * (`QualifyStep.tsx`'s `RungRow`), plus a "Currently previewing {name}" line
 * and a `Button variant="inline-text"` "Hide preview" wired straight to
 * `clearRungScope()`. Because `RungRow.isPreviewActive` reads the SAME
 * `rung-scope-store`, clicking Hide preview here clears the row's tint and
 * this panel's accent in the same store update — there is no separate
 * "un-tint the row" step to forget. The existing `rungScopeLabel()` sentence
 * is retained beneath the new line rather than replaced: it carries the
 * load-bearing first-match-wins explanation the short "Currently previewing"
 * line does not restate.
 *
 * ── BATCH 7 / UNIT E1 (plan `2026-09-01-003`) — `embedded` prop: on Qualify
 * this panel used to render its OWN bordered/padded card with its OWN
 * "Campaign members" heading INSIDE the `CollapsibleCard` `QualifyStep`
 * already wraps it in — a card-in-card with two competing headers. With
 * `embedded`, this component drops its outer border/padding and the heading
 * + matched-count/previewing cluster entirely, rendering just the status
 * line (`rungScopeLabel()`) and the table; `QualifyStep`'s own
 * `CollapsibleCardHeader` carries the matched-count/previewing cluster
 * instead, via the exported `MembersPanelHeaderStatus` (same derivation,
 * shared React Query cache / zustand store — see `useMembersPanelScopeState`
 * below). The scope tint moves to the host card too.
 * Default `false` — `SequencesStep`'s bare, non-collapsible mount is
 * unaffected and keeps the original card-with-its-own-heading chrome.
 *
 * ── 2026-09-16 FIX 3 — ONE TOKEN PAIR FOR "BEING PREVIEWED":
 * `border-border-selected` + `bg-bg-selected`, here AND on `RungRow`'s
 * previewed row AND on `QualifyStep`'s host `CollapsibleCard`. It used to be
 * `border-border-yellow-light` + `bg-bg-yellow-light`, which resolves to
 * `--color-lemon-800` in dark mode (`web/app-home/src/index.css`) and flooded
 * the whole table with a saturated mustard fill. Being previewed is a
 * SELECTION, not a warning — the selected pair is this app's existing token
 * for "the thing you picked" and has a real dark-mode value
 * (`--color-blueberry-900`). A fourth previewed-scope surface should use this
 * same pair. `SequencesStep`'s inline `bg-bg-warning` token chips are
 * deliberately NOT this: those really are warnings.
 *
 * Columns are the four the table now carries — name, EMAIL (2026-09-16 Fix 1
 * — a campaign can hold the same person three times under one name, so Name
 * alone is ambiguous), campaign-member status, CM created date — plus C3's
 * conditional "Missing fields" column
 * (below). No other member fields render here: this is chrome, not a roster
 * browser.
 *
 * ── C3 (plan `2026-09-01-001`, decisions #4/#5/#6): this table absorbed the
 * two behaviors of the deleted `RungRosterDrawer.tsx`, so that ONE table on
 * both Qualify and Sequences does what a drawer used to (Spencer's "get rid of
 * the previewing sidebar"):
 *
 *   • ROW SELECTION drives the Sequences step's copy preview. A row click
 *     writes to `sequence-preview-store.ts` (member + `editorMode='preview'`)
 *     rather than calling a prop, because this component is a SIBLING of the
 *     sequence editor — `PlayFlowShell` builds one element of it and hands it
 *     to each step as a slot, so there is no ancestor holding preview state to
 *     thread through. See that store's module doc. On Qualify and Review the
 *     same click just records the selection: harmless, and deliberately NOT
 *     special-cased per step (plan C3 item 5).
 *   • MISSING MERGE FIELDS, as a dedicated column AND the prototype's
 *     icon-in-name-cell + unresolved-sorted-to-top treatment
 *     (`sortMembersForScope`, ported from `playbooks-prototype-v6.html:
 *     6871-6893`). Both, not one instead of the other (decision #6).
 *
 * The gap check needs a SEQUENCE to check copy against, which only an active
 * rung scope with a mapping can name — so the column is gated on exactly that
 * and is absent on Qualify with no rung previewed, rather than rendering an
 * empty, meaningless column. A member has exactly one gap-set (first-match-wins
 * waterfall × one sequence per rung, decision #6), so there is no ambiguity
 * about WHICH mapping a row's missing fields belong to.
 *
 * Where the mapping comes from, in order: the STAGED playbook draft
 * (`playbook-draft-store.ts`) when it belongs to the scoped playbook — so a
 * mapping the rep just changed on this screen is reflected without a save —
 * else the live playbook head this component already queries. The copy itself
 * is the sequence head overlaid with any in-progress `sequence-copy-draft-store`
 * edits, the same live-copy basis `SequencesStep`'s own roster gap check uses,
 * so the two never disagree about what is unresolved.
 *
 * ── CODEX ROUND-4 FINDING #2: the ladder's cache key
 * (`matchedMembersLadderKey`) is `(campaignSfdcId, playbookId)`, not
 * version-qualified, and `WaterfallCard`'s reorder handler applies the new
 * order optimistically and only invalidates the ladder AFTER the server
 * write persists — so between "the reorder POST succeeds" and "the ladder
 * refetch lands", this panel could otherwise render a ladder response
 * computed under the OLD priority order. Closed the same way
 * `review-gate.ts#reviewMemberTally` already closes it for `ReviewStep`:
 * this component runs its OWN live `getPlaybook` query for the scoped rung's
 * Playbook (same query key `QualifyStep`/`ReviewStep` use, so it is normally
 * served from cache, not a second network round trip) and validates the
 * ladder's own `playbookVersionId` against that LIVE head version — not a
 * value captured once, back when Preview was clicked, which would leave a
 * scope permanently "stale" after any later reorder that legitimately
 * advances the head. `isFetching` (not just `isPending`) on both queries
 * feeds into `ScopedMatchState` too, so a background refetch — the version
 * genuinely not yet known to have caught up — also fails closed to
 * "matching…" rather than briefly re-rendering the previous, possibly stale
 * response.
 */
/**
 * Batch 7 (plan `2026-09-01-003` Unit E1) — the scope/match derivation shared
 * by `MembersPanel`'s own header (non-embedded mode) and
 * `MembersPanelHeaderStatus` (the header cluster `QualifyStep` renders in its
 * own `CollapsibleCardHeader` when it mounts this panel with `embedded`).
 * Both consumers read the SAME React Query cache entries / zustand store —
 * this is a second READ of already-shared state, not a second source of it,
 * so mounting it in two places at once cannot desync them.
 */
function useMembersPanelScopeState(campaignSfdcId: string, playId?: string) {
  const { apiFetch } = useApi();
  const { members, isLoading, isError } = useLauncherMembers(campaignSfdcId);
  const scope = useRungScopeStore(selectRungScope);
  const audienceDraft = useRungScopeStore(selectAudienceDraft);
  const label = rungScopeLabel(scope);
  // Item 9's short name — the Audience Rule variant carries no name field of
  // its own (unlike a rung, which is user-named), so it borrows the
  // accordion's own visible label (`QualifyStep.tsx`'s "Audience Rule"
  // heading) rather than inventing a second name for the same thing. U6
  // (R-Q1) — a rung-draft scope IS user-named (it carries the same
  // `rungName` a saved `'rung'` scope does), so it takes the same branch.
  const previewingName =
    scope === null ? null : scope.kind === 'rung' || scope.kind === 'rung-draft' ? scope.rungName : 'Audience Rule';

  // W2 (fix-round2-plan.md) — enabled for 'rung-draft' too, not just 'rung':
  // the rung-draft branch below now reduces its raw live-filter match by this
  // ladder (`matchedIdsForRungDraftScope`), so it needs the same ladder fetch
  // a saved rung's Preview already triggers.
  const ladderQuery = useMatchedMembersLadder(
    campaignSfdcId,
    scope?.kind === 'rung' || scope?.kind === 'rung-draft' ? scope.playbookId : null,
    playId,
  );
  const audienceQuery = useAudienceMatchedMembers(
    campaignSfdcId,
    audienceDraft,
    scope?.kind === 'audience',
  );
  // U6 (R-Q1) — the rung-draft's own UNSAVED, staged filter, as a raw live
  // match. Same `useFilterMatchedMembers` call shape (and cache key)
  // `RungRow`'s own inline count uses for the SAME scope, so Apply issues
  // exactly one request, not two. W2 (fix-round2-plan.md) — this raw match is
  // no longer the final answer on its own: the `match` derivation below runs
  // it through `matchedIdsForRungDraftScope` alongside `ladderQuery` and
  // `playbookQuery` to exclude members a higher-priority rung already
  // claims — see `RungScope`'s `'rung-draft'` doc comment.
  const rungDraftQuery = useFilterMatchedMembers(
    campaignSfdcId,
    scope?.kind === 'rung-draft' ? scope.groups : [],
    scope?.kind === 'rung-draft' ? scope.groupLogic : 'AND',
    scope?.kind === 'rung-draft',
  );
  // Same query key `QualifyStep`/`ReviewStep` use for this Playbook — shares
  // their cache entry rather than issuing a redundant fetch on top of it.
  // W2: widened to 'rung-draft' alongside `ladderQuery` above — both queries
  // feed `matchedIdsForRungDraftScope`'s version guard and exclusion set.
  const scopedPlaybookId =
    scope?.kind === 'rung' || scope?.kind === 'rung-draft' ? scope.playbookId : undefined;
  const playbookQuery = useQuery({
    queryKey: playbookDetailQueryKey(scopedPlaybookId ?? '', playId),
    queryFn: () => getPlaybook(apiFetch, scopedPlaybookId!, { playId }),
    enabled: !!scopedPlaybookId,
    // Codex hardening fix: same query key `QualifyStep`/`ReviewStep` use for
    // this Playbook, but THIS component also unmounts/remounts on every tab
    // switch (see the mounting note above) — under the app's global
    // `staleTime: 0` that remount refetched and flashed even though the
    // Playbook hadn't changed. `QualifyStep`'s `handleReorder` invalidates
    // `playbookDetailQueryKey(playbookId)` on its catch path (and writes
    // the new head directly via `setQueryData` on success), and
    // `ReviewStep`'s "Re-check" invalidates the broad `['playbooks']`
    // prefix — every write path that can move this Playbook already
    // explicitly invalidates it, so a numeric TTL is redundant.
    staleTime: Infinity,
  });

  // Only the query belonging to the CURRENT scope is read — the other one is
  // disabled, and a disabled React Query sits in `pending` forever.
  let match: ScopedMatchState = { isPending: false, isFetching: false, isError: false, matchedIds: null };
  // Round-3 (U25) — true only for a 'rung-draft' scope whose segmentation is
  // shared with a rung ABOVE it in the ladder (Decision 9, orchestrator
  // override); see `matchedIdsForRungDraftScope`'s doc. Drives which `Alert`
  // renders at the `'match-error'` branch below.
  let sharedSegmentationDraft = false;
  if (scope?.kind === 'rung') {
    match = {
      isPending: ladderQuery.isPending || playbookQuery.isPending,
      isFetching: ladderQuery.isFetching || playbookQuery.isFetching,
      isError: ladderQuery.isError || playbookQuery.isError,
      matchedIds: matchedIdsForRungScope(
        ladderQuery.data,
        scope.rungId,
        playbookQuery.data?.head?.versionId ?? null,
      ),
    };
  } else if (scope?.kind === 'audience') {
    match = {
      isPending: audienceQuery.isPending,
      isFetching: audienceQuery.isFetching,
      isError: audienceQuery.isError,
      matchedIds: audienceQuery.data?.matchedMemberIds ?? null,
    };
  } else if (scope?.kind === 'rung-draft') {
    // W2 (fix-round2-plan.md, Decision 5) — the raw live-filter match reduced
    // by every ladder rung ABOVE this rung's claimed members, via the SAME
    // `matchedIdsForRungDraftScope` helper `QualifyStep`'s `RungRow` uses for
    // its own inline "(preview)" count, so the panel's rows and that count
    // can never disagree about which members this draft would actually claim
    // if saved. `isPending`/`isFetching`/`isError` are OR-ed across all three
    // queries (the draft match, the ladder, and the live playbook head) —
    // any one of them still resolving means the ladder-aware answer isn't
    // trustworthy yet.
    sharedSegmentationDraft = rungDraftSegmentationIsShared(
      ladderQuery.data,
      scope.rungId,
      playbookQuery.data?.head?.versionId ?? null,
    );
    match = {
      isPending: rungDraftQuery.isPending || ladderQuery.isPending || playbookQuery.isPending,
      isFetching: rungDraftQuery.isFetching || ladderQuery.isFetching || playbookQuery.isFetching,
      isError: rungDraftQuery.isError || ladderQuery.isError || playbookQuery.isError,
      matchedIds: matchedIdsForRungDraftScope(
        ladderQuery.data,
        scope.rungId,
        playbookQuery.data?.head?.versionId ?? null,
        rungDraftQuery.data?.matchedMemberIds ?? null,
      ),
    };
  }

  const state = membersPanelState(scope, members, match);

  return { members, isLoading, isError, scope, label, previewingName, state, playbookQuery, sharedSegmentationDraft };
}

/**
 * PR C QA follow-ups U6 (items 8, 9) header cluster — the matched-count text
 * and the "Currently previewing {name}" / "Hide preview" pair. Broken out
 * (Batch 7 / Unit E1) so `QualifyStep` can render it directly inside its own
 * `CollapsibleCardHeader` when it mounts `MembersPanel` with `embedded`: the
 * host header then carries what `MembersPanel`'s own "Campaign members"
 * heading used to show, instead of a second competing heading. `MembersPanel`
 * itself still renders this same cluster inline when NOT embedded (the shape
 * `SequencesStep`'s bare, non-collapsible mount keeps).
 */
export function MembersPanelHeaderStatus({ campaignSfdcId }: { campaignSfdcId: string }) {
  const { members, scope, previewingName, state } = useMembersPanelScopeState(campaignSfdcId);
  const { campaign } = useLauncherCampaign(campaignSfdcId); // cache hit — PlayFlowShell already reads this key
  if (state.kind !== 'matched' && scope === null && !campaign?.members_last_synced_at) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {campaign?.members_last_synced_at && (
        <Text size="xs" variant="tertiary" className="tabular-nums">
          Roster as of {new Date(campaign.members_last_synced_at).toLocaleString()}
        </Text>
      )}
      {state.kind === 'matched' && (
        <Text size="xs" variant="tertiary" className="tabular-nums">
          {state.totalMatched} matched of {members.length}
        </Text>
      )}
      {scope !== null && (
        <div className="flex items-center gap-2">
          <Text size="xs" variant="tertiary">
            Currently previewing {previewingName}
          </Text>
          <Button
            variant="inline-text"
            size="xs"
            onClick={() => {
              clearRungScope();
              setSequenceEditorMode('edit');
            }}
          >
            Hide preview
          </Button>
        </div>
      )}
    </div>
  );
}

export function MembersPanel({
  campaignSfdcId,
  embedded = false,
  playId,
}: {
  campaignSfdcId: string;
  /**
   * Play isolation (plan 2026-09-25-002, U5) — the play this panel is
   * mounted in, so the ladder read (`GET /campaign-members/ladder?…&playId=`)
   * and the internal playbook read bucket by this play's own ladder
   * (Decision 12). The "Missing fields" gap check's sequence read does NOT
   * take this: a play-owned sequence is its own identity, so that read is
   * always the same unscoped `GET /sequences/:id` the playbook doorway uses.
   * `undefined` in the playbook doorway.
   */
  playId?: string;
  /**
   * Batch 7 / Unit E1 — drops the outer bordered/padded card and the
   * "Campaign members" heading + matched-count/previewing cluster (the
   * host's own `CollapsibleCardHeader` carries those instead, via
   * `MembersPanelHeaderStatus`); the scope tint moves to the host card too.
   * Default off — `SequencesStep`'s bare, non-collapsible mount is
   * unaffected.
   */
  embedded?: boolean;
}) {
  const { apiFetch } = useApi();
  const { members, isLoading, isError, scope, label, previewingName, state, playbookQuery, sharedSegmentationDraft } =
    useMembersPanelScopeState(campaignSfdcId, playId);
  const rows = state.kind === 'roster' || state.kind === 'matched' ? state.members : [];

  // QA round 7, U7 (item 13) — which member's detail drawer is open, if any.
  // Deliberately separate from `sequence-preview-store`'s `selectedMemberId`:
  // that's the copy-preview scope (a Name click), this is a read-only detail
  // view (an Info click) — the two affordances must stay independent so a
  // rep opening the drawer never silently changes which member's copy is
  // being previewed.
  const [detailMemberId, setDetailMemberId] = useState<string | null>(null);

  // ── C3: the scoped rung's sequence mapping — the whole gate for the
  // "Missing fields" column. Staged draft first (a mapping the rep changed on
  // this screen but hasn't saved), and only for the playbook the scope names:
  // the draft store holds exactly one playbook's draft, and reading another
  // playbook's mapping here would annotate rows against a sequence this rung
  // does not use.
  const draftPlaybookId = usePlaybookDraftStore((s) => s.playbookId);
  const playbookDraft = usePlaybookDraftStore(selectPlaybookDraft);
  const scopedMapping = useMemo(() => {
    if (scope?.kind !== 'rung') return null;
    const staged =
      draftPlaybookId === scope.playbookId
        ? playbookDraft?.rungs.find((rung) => rung.id === scope.rungId)?.mapping
        : undefined;
    if (staged) return staged;
    return playbookQuery.data?.head?.rungs.find((rung) => rung.id === scope.rungId)?.mapping ?? null;
  }, [scope, draftPlaybookId, playbookDraft, playbookQuery.data]);

  // Same query key `SequencesStep`/`SequenceEditorShell` use for this
  // sequence — never play-scoped (Decision 12: a play-owned sequence is its
  // own identity) — so a rung previewed from either step shares one cache
  // entry rather than issuing a second fetch.
  const gapSequenceQuery = useQuery({
    queryKey: sequenceDetailQueryKey(scopedMapping?.sequenceId ?? ''),
    queryFn: () => getSequence(apiFetch, scopedMapping!.sequenceId),
    enabled: !!scopedMapping,
  });
  // The real campaign, not `null` — U10's fix: a hardcoded `null` made every
  // campaign-scoped token (`{{Event Name}}`/`{{Event Date}}`/`{{Campaign
  // Type}}`) resolve empty and false-positive as a gap for every member. Same
  // hook (and cache entry) `QualifyStep`'s `ContextCard` and `SequencesStep`
  // already use for this campaign.
  const { campaign } = useLauncherCampaign(campaignSfdcId);
  /**
   * Playbooks QA round 4, U11 (E1) / Decision 14 — SCOPED to the previewed
   * rung's own draft scope + sequence, shallow-compared.
   *
   * It used to subscribe to the whole `entries` map, which `editCopyDraft`
   * replaces on every keystroke — so typing in ANY rung's step editor
   * re-rendered this entire 400-row table and re-ran its roster gap scan,
   * even when the rep was editing a rung this panel is not previewing.
   *
   * `''` for both parts when nothing is scoped (no rung previewed, or no
   * mapping to check copy against): no real key can carry an empty scope or
   * sequence id, so the selector returns `{}` and the `liveSteps` memo below
   * short-circuits on its own guards anyway.
   */
  const copyDraftEntries = useSequenceCopyDraftStore(
    useShallow(
      selectCopyDraftEntriesFor(
        scope?.kind === 'rung' ? rungDraftKey(scope.rungId) : '',
        scopedMapping?.sequenceId ?? '',
      ),
    ),
  );
  // 2026-09-02 rip-out (U3) — this used to resolve a lock-at-send pin or an
  // in-play branch row ahead of the sequence's head (Codex Issue 4's mirror
  // of `SequencesStep`'s own `branchedSequencePins`/`branchPin`/`mappingPin`).
  // Both concepts are gone: the gap check now reads exactly the Sequence's
  // current head, unconditionally, matching `SequencesStep`'s own
  // `renderedVersion` (which does the same).
  const liveSteps = useMemo(() => {
    const savedSteps = gapSequenceQuery.data?.head?.steps;
    if (!savedSteps || !scopedMapping || scope?.kind !== 'rung') return [];
    // D1 shared-edit-leak fix — scoped by the previewed rung's OWN draft
    // scope (`rungDraftKey(scope.rungId)`, reused from `playbook-draft.ts`
    // rather than re-derived), not by sequence alone, so this gap check
    // reads only what THIS rung's own editor has typed.
    const draftScope = rungDraftKey(scope.rungId);
    return savedSteps.map((step) =>
      resolveCopyDraftValues(copyDraftEntries[copyDraftKey(draftScope, scopedMapping.sequenceId, step.position)], {
        subject: step.subject ?? '',
        body: step.body ?? '',
      }),
    );
  }, [gapSequenceQuery.data, copyDraftEntries, scopedMapping, scope]);
  // Keyed on the row IDS rather than the `rows` array: `membersPanelState`
  // filters a fresh array on every render, so an array-identity dep would
  // re-run the per-member interpolation on every unrelated re-render.
  const rowIdKey = rows.map((member) => member.id).join(',');
  /**
   * U11 (E1) — the roster scan runs against the DEFERRED live steps, so a
   * keystroke in the step editor commits first and this table's gap column
   * catches up a beat later. Deferring `liveSteps` rather than the computed
   * `gaps` (the plan's own default) defers the expensive work itself, not just
   * the moment its result appears.
   */
  const deferredLiveSteps = useDeferredValue(liveSteps);
  /**
   * R5-4 — the sender these steps will actually send as: the mapped
   * sequence's LIVE `default_sender`, not `scopedMapping`'s stale copy. This
   * panel has no `PlaybooksLibraryResponse` in scope (it scopes members, not
   * the library), but `gapSequenceQuery` already fetches this exact
   * sequence's detail (`PlaybooksSequenceDetailResponse.sequence`, the same
   * `PlaybooksSequenceSummary` shape the library carries) for the roster gap
   * check just above — that's the same fact `effectiveSender` needs, with no
   * second fetch. `gapSequenceQuery.data` is `undefined` while loading, which
   * is exactly `effectiveSender`'s loading-gap case.
   */
  const gaps = useMemo(
    () => rosterTokenGaps(deferredLiveSteps, rows, campaign, effectiveSender(gapSequenceQuery.data?.sequence, scopedMapping)),
    [deferredLiveSteps, rowIdKey, campaign, gapSequenceQuery.data?.sequence?.sender, scopedMapping?.sender],
  );
  /**
   * U11 (E1) — the inverted index, built ONCE per `gaps`, replacing the
   * per-row `missingTokensForMember(gaps, member.id)` call below. That helper
   * scans `gap.memberIds` (roster-sized) for every rendered row, so the table
   * cost O(rows · gaps · members) per render; this is O(gaps · members) once
   * and O(1) per row.
   */
  const missingByMemberId = useMemo(() => missingTokensByMemberId(gaps), [gaps]);
  /** The column exists only when there is a mapping to check copy against (C3 — no empty column on Qualify). */
  const showGapColumn = scopedMapping !== null;
  // A3 (plan `2026-09-01-003`) — the direction is stored, not component
  // state (see `members-panel-collapse-store.ts`'s module doc: this panel
  // unmounts on every tab switch, which would silently reset a `useState`).
  const gapSortDirection = useMembersPanelCollapseStore(selectGapSortDirection);
  /**
   * U11 (E1) — memoized. `sortMembersForScope` copies and sorts the whole
   * roster; unmemoized it re-ran on every render of this panel, including
   * renders caused by an unrelated store change. Keyed on `rowIdKey` (not the
   * `rows` array, which `membersPanelState` rebuilds every render — the same
   * reasoning the `gaps` memo above already documents).
   */
  const { list: sortedRows, unresolvedIds } = useMemo(
    () => sortMembersForScope(rows, gaps, gapSortDirection),
    // `rowIdKey` deliberately stands in for `rows` (see the doc above);
    // `react-hooks/exhaustive-deps` is off repo-wide (`eslint.config.mjs:42`),
    // so no disable directive is needed — adding one is itself an
    // unused-directive lint error.
    [rowIdKey, gaps, gapSortDirection],
  );
  /**
   * QA round 7, U7 (item 13) / Codex fixes 2026-09-18, F7 — the open drawer's
   * member, resolved against `sortedRows`: the SAME memoized objects the
   * table renders, never the live `members` array. `sortedRows` is keyed on
   * `rowIdKey` on purpose (its doc above), so a background refetch that
   * changes a field without changing the id set leaves the rows on the
   * previous objects — a drawer reading `members` showed the NEW status next
   * to a row still showing the OLD one, for the same person. One source, one
   * snapshot: the drawer can only ever say what its row says, and it catches
   * up exactly when the row does. `rows ⊆ members` in every state
   * (`membersPanelState` copies or filters `members`), so nothing the drawer
   * could show before is unreachable now; a member who leaves the rows (a
   * Preview scope that stops matching them, or the transient `'matching'`
   * state) closes the drawer, which is what the table does with the row.
   */
  const detailMember = useMemo(
    () => (detailMemberId === null ? null : (sortedRows.find((member) => member.id === detailMemberId) ?? null)),
    [sortedRows, detailMemberId],
  );
  const selectedMemberId = useSequencePreviewStore(selectPreviewMemberId);
  // A matched id the loaded roster doesn't carry (roster fetched before a sync
  // added rows). Reported rather than swallowed — the count and the table would
  // otherwise silently disagree.
  const missingFromRoster =
    state.kind === 'matched' ? state.totalMatched - state.members.length : 0;

  // Batch 7 / Unit E1 — the "status line + table" content is IDENTICAL in
  // embedded and non-embedded mode; only the wrapper (border/padding) and the
  // heading above it differ. Factored out once so the two modes cannot drift.
  const bodyContent = (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-6">
          <LoadingSpinner size="sm" />
        </div>
      )}

      {isError && !isLoading && (
        <Alert status="error">Couldn&apos;t load campaign members. Please try again.</Alert>
      )}

      {!isLoading && !isError && state.kind === 'matching' && (
        <div className="flex items-center justify-center gap-2 py-6">
          <LoadingSpinner size="sm" />
          <Text size="xs" variant="tertiary">
            Matching members…
          </Text>
        </div>
      )}

      {!isLoading && !isError && state.kind === 'match-error' && (
        sharedSegmentationDraft ? (
          // Round-3 (U25) — a rung-above sharing this rung's segmentation is
          // an EXPLAINABLE reason the preview can't be trusted, not the
          // generic "couldn't match" failure below — see
          // `RUNG_DRAFT_SHARED_SEGMENTATION_NOTE`'s doc.
          <Alert status="info">{RUNG_DRAFT_SHARED_SEGMENTATION_NOTE}</Alert>
        ) : (
          <Alert status="error">
            Couldn&apos;t work out which members this selection matches. Press Re-check on the Review
            step, or pick the group again.
          </Alert>
        )
      )}

      {!isLoading && !isError && state.kind === 'roster' && members.length === 0 && (
        <Text size="sm" variant="tertiary">
          No members synced for this campaign yet.
        </Text>
      )}

      {!isLoading && !isError && state.kind === 'matched' && state.totalMatched === 0 && (
        <Text size="sm" variant="tertiary">
          No campaign members match this selection.
        </Text>
      )}

      {missingFromRoster > 0 && (
        <Text size="xs" variant="tertiary">
          {missingFromRoster === 1
            ? '1 matched member is not in the loaded roster'
            : `${missingFromRoster} matched members are not in the loaded roster`}{' '}
          — Re-check to refresh it.
        </Text>
      )}

      {/*
        2026-09-16 Fix 2 — the scope caption CAPTIONS the table, so it sits
        directly above it rather than in a band of its own at the very top of
        the body, and it uses the same `size="sm" variant="tertiary"` type as
        a card's header description instead of the smaller `xs`. It still
        renders in every state (loading / empty / matched): it says what the
        panel is scoped to, which is true whether or not there are rows.
      */}
      <Text size="sm" variant="tertiary">
        {label}
      </Text>

      {!isLoading && !isError && sortedRows.length > 0 && (
        <div className="max-h-64 overflow-y-auto">
          <Table dense>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                {/*
                  2026-09-16 Fix 1 — a campaign can carry the SAME person three
                  times (three `launcher_sfdc_campaign_members` rows, identical
                  `contact_name`, different `contact_email`), so Name alone
                  cannot tell a rep which row they just clicked into the copy
                  preview. Unconditional, unlike "Missing fields": every member
                  row has an email identity to show, scope or no scope.
                */}
                <TableHeader>Email</TableHeader>
                {showGapColumn && (
                  // A3 — a real, keyboard-reachable sort toggle (checked
                  // Glaze's own `Table` for a built-in sortable-header
                  // affordance first: it exists, but its 3-state asc/desc/
                  // clear cycle doesn't fit this column's plain 2-state
                  // toggle without contorting the data model, so this is the
                  // plain button+caret the plan calls out as the fallback).
                  <TableHeader aria-sort={gapSortDirection === 'missing-first' ? 'ascending' : 'descending'}>
                    <button
                      type="button"
                      className="flex items-center gap-1"
                      onClick={() =>
                        setGapSortDirection(gapSortDirection === 'missing-first' ? 'missing-last' : 'missing-first')
                      }
                    >
                      Missing fields
                      <Icon
                        type={gapSortDirection === 'missing-first' ? CaretUp : CaretDown}
                        size="xs"
                        aria-hidden="true"
                      />
                    </button>
                  </TableHeader>
                )}
                <TableHeader>CM status</TableHeader>
                <TableHeader>CM created date</TableHeader>
                {/* QA round 7, U7 (item 13) — the member-detail drawer trigger. No text label (the icon + the cell's own accessible button carry the meaning); the column header exists only so screen-reader table navigation names the column. */}
                <TableHeader aria-label="Details" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((member) => {
                const { label, emailCell } = memberDisplay(member);
                const isUnresolved = unresolvedIds.has(member.id);
                // U11 (E1) — O(1) lookup. A member with no gaps is absent
                // from the map, which `?? []` turns into the empty cell.
                const missing = showGapColumn ? missingByMemberId.get(member.id) ?? [] : [];
                return (
                  <TableRow
                    key={member.id}
                    className={selectedMemberId === member.id ? 'bg-bg-blue-light' : undefined}
                  >
                    <MemberNameCell
                      member={member}
                      label={label}
                      selected={selectedMemberId === member.id}
                      unresolved={isUnresolved}
                      onSelect={() => selectPreviewMember(member.id)}
                    />
                    <MemberEmailCell emailCell={emailCell} />
                    {showGapColumn && (
                      <TableCell>
                        {missing.length > 0 ? (
                          <Text size="xs" className="text-content-warning">
                            {missing.join(', ')}
                          </Text>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    )}
                    <MemberStatusCell member={member} />
                    <MemberCreatedCell member={member} />
                    {/*
                      QA round 7, U7 (item 13) — opens the read-only detail
                      drawer. Deliberately a SEPARATE control from the Name
                      button: the Name click's job (scoping the copy preview)
                      is a single, unambiguous gesture and must stay that way
                      — it does not also open the drawer.
                    */}
                    <MemberDetailsButton label={label} onClick={() => setDetailMemberId(member.id)} />
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {detailMember && <MemberDetailDrawer member={detailMember} onClose={() => setDetailMemberId(null)} />}
    </>
  );

  if (embedded) {
    // No outer border/padding, no "Campaign members" heading, no
    // matched-count/previewing cluster, no scope tint — the host
    // `CollapsibleCard` (`QualifyStep`) carries all of that via
    // `MembersPanelHeaderStatus` and its own card className.
    return <div className="flex flex-col gap-2">{bodyContent}</div>;
  }

  return (
    <div
      className={`flex flex-col gap-2 rounded-lg border p-4 transition-colors ${
        scope !== null ? 'border-border-selected bg-bg-selected' : 'border-border-primary bg-bg-primary'
      }`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-baseline gap-2">
          <Text size="sm" weight="semibold">
            Campaign members
          </Text>
          {state.kind === 'matched' && (
            <Text size="xs" variant="tertiary" className="tabular-nums">
              {state.totalMatched} matched of {members.length}
            </Text>
          )}
        </div>
        {scope !== null && (
          <div className="flex items-center gap-2">
            <Text size="xs" variant="tertiary">
              Currently previewing {previewingName}
            </Text>
            {/*
              C3: also leaves the sequence editor's Preview mode. Before C3
              this only unscoped the table, which was complete then — the mode
              lived in component state this panel could not reach. Now that it
              lives in `sequence-preview-store.ts`, unscoping WITHOUT leaving
              Preview would strand the rep in a preview of a rung the table has
              stopped showing.
            */}
            <Button
              variant="inline-text"
              size="xs"
              onClick={() => {
                clearRungScope();
                setSequenceEditorMode('edit');
              }}
            >
              Hide preview
            </Button>
          </div>
        )}
      </div>
      {bodyContent}
    </div>
  );
}
